import React, { useEffect, useRef } from 'react';

// Accessible modal dialog primitive (WCAG 2.2 AA).
// Moves focus into the dialog on open, keeps Tab and Shift+Tab inside it,
// closes the topmost open dialog on Escape and returns focus to the opener.

export const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Module-level stack of open dialogs. Only the last entry responds to keys.
const openDialogStack = [];

const getFocusable = (container) => Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

// Last element focused outside any dialog. An autoFocus field inside a new
// dialog takes focus before effects run, so activeElement alone can point
// into the dialog instead of at the control that opened it.
let lastOutsideFocus = null;
if (typeof document !== 'undefined') {
  document.addEventListener('focusin', (event) => {
    if (!event.target.closest?.('[role="dialog"]')) lastOutsideFocus = event.target;
  });
}

export function useDialogA11y(ref, { onClose, initialFocusRef } = {}) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const active = document.activeElement;
    const opener = active && !dialog.contains(active) ? active : lastOutsideFocus;
    const entry = { dialog };
    openDialogStack.push(entry);

    const initial = (initialFocusRef && initialFocusRef.current) || getFocusable(dialog)[0];
    if (initial) {
      initial.focus();
    } else {
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus();
    }

    const handleKey = (event) => {
      if (openDialogStack[openDialogStack.length - 1] !== entry) return;
      if (event.key === 'Escape') {
        if (typeof closeRef.current === 'function') {
          event.preventDefault();
          closeRef.current();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = getFocusable(dialog);
      if (controls.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      const outside = !dialog.contains(active) || active === dialog;
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      const index = openDialogStack.indexOf(entry);
      if (index !== -1) openDialogStack.splice(index, 1);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [ref, initialFocusRef]);
}

export default function ModalDialog({ onClose, labelledBy, initialFocusRef, className, children, ...rest }) {
  const dialogRef = useRef(null);
  useDialogA11y(dialogRef, { onClose, initialFocusRef });
  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className={className}
      {...rest}
    >
      {children}
    </div>
  );
}
